import { HashrateItem } from "@components/Charts/ChartDatasetUtils";
import { locationsCacheKey } from "@components/Pages/Locations/useLocationsControl";
import { MINER_STATUSES_E } from "@constants/MinerStatuses";
import { createTyper } from "@mobx/services/http";
import {
  ILocation3,
  ILocationStringsData,
  IRackItem3,
  IRackSection3,
  IUnrackItem3,
  IUpdateRackPosition3,
} from "@mobx/store/locations/types";
import { DatePeriod } from "@services/http/dto";
import { ILocationForList, LocationMeta } from "@services/locations";
import {
  AddRackResponse,
  AddTemplateResponse,
  EditRackResponse,
  IBuildingInfo,
  IMoveRackItemsRecord,
  IUpdateRackPosition,
  MergeRackItemResponse,
  RackListResponse
} from "@services/rackService";
import W2UiFilterBuilder from "@services/w2ui";
import { SearchItem } from "@services/w2ui/w2const";
import { IAvailableRack } from "@store/availableLocationRacks/actionTypes";
import buildFormData from "@utils/buildFormData";
import { clearCache } from "@utils/localCache";
import {
  getUtcFormat,
  getUtcToday,
  getUtcTomorrow
} from "@utils/moscowTime";
import { Moment } from "moment";
import {
  castResponse,
  Method,
  RecordResponse,
  RecordsResponse,
  RequestArgs,
  RequestHandle
} from "wl-net";
import { createDatePeriodParamsEx3 } from "./utils";

function createDatePeriodParamsEx(
  dateFrom: Moment | null,
  dateTo: Moment | null
): DatePeriod {
  return {
    date_from: dateFrom ? getUtcFormat(dateFrom) : getUtcToday(), //getServerToday(),
    date_to: dateTo ? getUtcFormat(dateTo) : getUtcTomorrow(),
  };
}

// /admin/object/list
// returns RecordsResponse<IBuildingInfo>
export const GetObjectList_RequestTypes = createTyper({
  makeRequest(
    handle: RequestHandle,
    location_id: number
  ): RequestArgs<{ location_id: number }> {
    return {
      handle,
      method: Method.Post,
      URL: `/admin/object/list`,
      payload: { location_id },
    };
  },
  makeResponse: castResponse<RecordsResponse<IBuildingInfo>>,
});

export const DeleteBuilding_RequestTypes = createTyper({
  makeRequest(
    handle: RequestHandle,
    buildingID: number
  ): RequestArgs<{ id: string }> {
    return {
      handle,
      method: Method.Post,
      URL: `/admin/object/delete`,
      payload: { id: `${buildingID}` },
    };
  },
});

export const AddBuilding_RequestTypes = createTyper({
  makeRequest(
    handle: RequestHandle,
    locationID: number,
    name: string,
  ): RequestArgs<{ location_id: string, name: string }> {
    return {
      handle,
      method: Method.Post,
      URL: `/admin/object/add`,
      payload: { location_id: `${locationID}`, name },
    };
  },
  makeResponse: castResponse<RecordResponse<string>>,
});

// /admin/miner/statistic2
// returns RecordResponse<HashrateItem[]>
// export const GetStatistic_RequestTypes = createTyper({
//   makeRequest(
//     handle: RequestHandle,
//     dateFrom: Moment | null,
//     dateTo: Moment | null,
//     id: string,
//     typ: "location_id" | "object_id",
//     isAdmin?: boolean
//   ): RequestArgs<DatePeriod & { [key: string]: string }> {
//     const params = {
//       ...createDatePeriodParamsEx(dateFrom, dateTo),
//       [typ]: id
//     };
//     return {
//       handle,
//       method: Method.Post,
//       URL: `/${isAdmin ? "admin" : "user"}/miner/statistic2`,
//       payload: params,
//     };
//   },
//   makeResponse: castResponse<RecordResponse<HashrateItem[]>>,
// });

// /admin/rack/list2
// returns RackListResponse
export const GetRackList_RequestTypes = createTyper({
  makeRequest(
    handle: RequestHandle,
    location_id: number,
    object_id: number,
    search?: string,
    status?: MINER_STATUSES_E
  ): RequestArgs<string> {
    const builder = new W2UiFilterBuilder();
    if (search && typeof search === "string") {
      builder.addSearch(
        SearchItem.textContains("searching", search.replaceAll("-", ""))
      );
    }
    if (status !== undefined) {
      builder.addSearch(SearchItem.intIs("status", status));
    }
    let p = builder.build().stringParams;
    p +=
      (p === "" ? "" : "&") +
      `location_id=${location_id}&object_id=${object_id}`;
    return {
      handle,
      method: Method.Post,
      URL: `/admin/rack/list2`,
      payload: p,
    };
  },
  makeResponse: castResponse<RackListResponse>,
});

// /admin/object/add
// returns RecordResponse<string>
export const AddObject_RequestTypes = createTyper({
  makeRequest(
    handle: RequestHandle,
    location_id: string,
    name: string
  ): RequestArgs<{ location_id: string; name: string }> {
    return {
      handle,
      method: Method.Post,
      URL: `/admin/object/add`,
      payload: { location_id, name },
    };
  },
  makeResponse: castResponse<RecordResponse<string>>,
});

// /admin/rack/template/list
// returns RecordsResponse<IAvailableRack>
export const GetRackTemplatesList_RequestTypes = createTyper({
  makeRequest(
    handle: RequestHandle
  ): RequestArgs<{}> {
    return {
      handle,
      method: Method.Post,
      URL: `/admin/rack/template/list`,
      payload: {},
    };
  },
  makeResponse: castResponse<RecordsResponse<IAvailableRack>>,
});

// /admin/rack/template/add
// returns AddTemplateResponse
export const AddRackTemplate_RequestTypes = createTyper({
  makeRequest(handle: RequestHandle, sections: IRackSection3[], width: number, height: number):
    RequestArgs<{ rack_type: string; rack_items: string }> {

    const rack_type = `${width},${height}`;
    const rack_items = sections.map((r) => r.pos).join(",");;

    return {
      handle,
      method: Method.Post,
      URL: `/admin/rack/template/add`,
      payload: { rack_type, rack_items },
    };
  },
  makeResponse: castResponse<AddTemplateResponse>,
});

interface IEditRackParams {
  readonly rack_items: string;
  readonly rack_type: string;
  readonly id: string;
}

export const EditRackTemplate_RequestTypes = createTyper({
  makeRequest(handle: RequestHandle, templateID: string, sections: IRackSection3[], width: number, height: number): RequestArgs<IEditRackParams> {
    const rack_items = sections.map((r) => r.pos).join(",");
    const rack_type = `${width},${height}`;
    return {
      handle,
      method: Method.Post,
      URL: `/admin/rack/template/update`,
      payload: {
        rack_items, rack_type, id: templateID,
      },
    };
  },
  makeResponse: castResponse<AddTemplateResponse>,
});

// /admin/rack/add
// returns AddRackResponse
export const AddRack_RequestTypes = createTyper({
  makeRequest(
    handle: RequestHandle,
    locationID: number,
    objectID: number,
    templateID: string,
    x: number,
    y: number
  ): RequestArgs<{ location_id: string; object_id: string; template_id: string; x: number; y: number }> {
    return {
      handle,
      method: Method.Post,
      URL: `/admin/rack/add`,
      payload: { location_id: `${locationID}`, object_id: `${objectID}`, template_id: templateID, x: x + 1, y: y + 1 },
    };
  },
  makeResponse: castResponse<AddRackResponse>,
});

// /admin/unrack/move
// returns RecordResponse<IRackItem>
export const MoveUnrackItem_RequestTypes = createTyper({
  makeRequest(
    handle: RequestHandle,
    itemID: number,
    targetItemID: number,
  ): RequestArgs<{ id: string; rack_item_id: string }> {
    return {
      handle,
      method: Method.Post,
      URL: `/admin/unrack/move`,
      payload: { id: `${itemID}`, rack_item_id: `${targetItemID}` },
    };
  },
  makeResponse: castResponse<RecordResponse<IRackItem3>>,
});

// /admin/rack/item/move
// returns RecordResponse<IMoveRackItemsRecord>
export const MoveRackItem_RequestTypes = createTyper({
  makeRequest(
    handle: RequestHandle,
    id: number,
    targetItems: number[]
  ): RequestArgs<FormData> { // Payload is FormData
    const data = buildFormData({
      id: `${id}`,
      to_items: JSON.stringify(targetItems.map(item => `${item}`)),
    });
    return {
      handle,
      method: Method.Post,
      URL: `/admin/rack/item/move`,
      payload: data,
    };
  },
  makeResponse: castResponse<RecordResponse<IMoveRackItemsRecord>>,
});

// /admin/rack/item/merge
// returns MergeRackItemResponse
export const MergeRackItem_RequestTypes = createTyper({
  makeRequest(
    handle: RequestHandle,
    id: number,
    merge_id: number
  ): RequestArgs<{ id: string; merge_id: string }> {
    return {
      handle,
      method: Method.Post,
      URL: `/admin/rack/item/merge`,
      payload: { id: id.toString(), merge_id: merge_id.toString() },
    };
  },
  makeResponse: castResponse<MergeRackItemResponse>,
});

// /admin/rack/item/unmerge
// returns BaseResponse
export const UnmergeRackItem_RequestTypes = createTyper({
  makeRequest(
    handle: RequestHandle,
    id: number
  ): RequestArgs<{ id: string }> {
    return {
      handle,
      method: Method.Post,
      URL: `/admin/rack/item/unmerge`,
      payload: { id: id.toString() },
    };
  },
});

export type OperateRacksHistoryResponse = RecordResponse<[boolean, boolean]>;

// /admin/undo
// /admin/redo
// returns BaseResponse
export const OperateRacksHistory_RequestTypes = createTyper({
  makeRequest(
    handle: RequestHandle,
    undo: boolean
  ): RequestArgs<{}> {
    return {
      handle,
      method: Method.Post,
      URL: `/admin/${undo ? "undo" : "redo"}`,
      payload: {},
    };
  },
  makeResponse: castResponse<OperateRacksHistoryResponse>,
});

// rack/update/position
// returns BaseResponse
// export const UpdateRackPosition_RequestTypes = createTyper({
//   makeRequest(
//     handle: RequestHandle,
//     id: number,
//     x: number,
//     y: number,
//   ): RequestArgs<IUpdateRackPosition> {
//     return {
//       handle,
//       method: Method.Post,
//       URL: `/admin/rack/update/position`,
//       payload: {
//         id: `${id}`, x, y
//       },
//     };
//   },
// });

export const UpdateRackBLI_RequestTypes = createTyper({
  makeRequest(
    handle: RequestHandle,
    id: number,
    BLI: number,
  ): RequestArgs<{ id: string; rackid: number }> {
    return {
      handle,
      method: Method.Post,
      URL: `/admin/rack/update/rackid`,
      payload: {
        id: `${id}`,
        rackid: BLI,
      },
    };
  },
});


export const EditRack_RequestTypes = createTyper({
  makeRequest(
    handle: RequestHandle,
    rackID: number,
    sections: IRackSection3[],
    width: number,
    height: number,
  ): RequestArgs<FormData> {
    const rack_type = `${width},${height}`;
    const rack_items = JSON.stringify(
      sections.map((item) => {
        return { pos: item.pos, x: item.x, y: item.y };
      })
    );
    const data = buildFormData({ rack_items, rack_type, id: rackID });
    return {
      handle,
      method: Method.Post,
      URL: `/admin/rack/update`,
      payload: data,
    };
  },

  makeResponse: castResponse<EditRackResponse>,
});

// rack/update/positions
// returns BaseResponse
export const UpdateRacksPositions_RequestTypes = createTyper({
  makeRequest(
    handle: RequestHandle,
    ...args: IUpdateRackPosition3[]
  ): RequestArgs<{ id: string; x: string; y: string }> {
    const params = {
      id: args.map((a) => a.id).join(","),
      x: args.map((a) => a.x).join(","),
      y: args.map((a) => a.y).join(","),
    };
    return {
      handle,
      method: Method.Post,
      URL: `/admin/rack/update/positions`,
      payload: params,
    };
  },
});

// rack/delete
// returns BaseResponse
export const DeleteRack_RequestTypes = createTyper({
  makeRequest(
    handle: RequestHandle,
    id: number
  ): RequestArgs<{ id: string }> {
    return {
      handle,
      method: Method.Post,
      URL: `/admin/rack/delete`,
      payload: { id: `${id}` },
    };
  },
});

// location/list
// returns RecordsResponse<ILocationForList>
export const GetLocations_RequestTypes = createTyper({
  makeRequest(
    handle: RequestHandle,
    offset: number = 0,
    limit: number = 20,
    search?: string
  ): RequestArgs<string> {
    const builder = new W2UiFilterBuilder().setLimit(limit).setOffset(offset);
    if (search && typeof search === "string") {
      builder.setSearch([SearchItem.textContains("search", search)]);
    }
    const params = builder.build().stringParams;
    return {
      handle,
      method: Method.Post,
      URL: "/location/list",
      payload: params,
    };
  },
  makeResponse: castResponse<RecordsResponse<ILocationForList>>,
});

// location/delete
// returns BaseResponse
export const DeleteLocation_RequestTypes = createTyper({
  makeRequest(
    handle: RequestHandle,
    id: number
  ): RequestArgs<{ id: string }> {
    clearCache(locationsCacheKey);
    return {
      handle,
      method: Method.Post,
      URL: "/admin/location/delete",
      payload: { id: `${id}` },
    };
  },
});

export const AddLocation_RequestTypes = createTyper({
  makeRequest(
    handle: RequestHandle,
    params: ILocationStringsData,
  ): RequestArgs<{
    name: string;
    locId: string;
    address?: string;
    coords?: string;
    notes?: string;
  }> {
    return {
      handle,
      method: Method.Post,
      URL: "/admin/location/add",
      payload: {
        name: params.Name.trim(),
        locId: params.Code.trim(),
        address: params.Address?.trim() || undefined,
        coords: params.Coord?.trim() || undefined,
        notes: params.Notes?.trim() || undefined,
      },
    };
  },
  makeResponse: castResponse<RecordResponse<ILocation3>>,
});


export const EditLocationMeta_RequestTypes = createTyper({
  makeRequest(handle: RequestHandle, id: number, meta?: string | LocationMeta) {
    let params = { id: `${id}`, meta, edit: true };
    if (meta !== undefined && typeof meta !== "string") {
      params = {
        ...params,
        meta: JSON.stringify(params.meta),
      }
    }
    return {
      handle,
      method: Method.Post,
      URL: `/admin/location/add`,
      payload: { ...params },
    }
  },
  makeResponse: castResponse<RecordResponse<string>>,
});

export const EditLocation_RequestTypes = createTyper({
  makeRequest(
    handle: RequestHandle,
    id: number,
    params: ILocationStringsData,
  ): RequestArgs<{
    id: string;
    name: string;
    locId: string;
    address?: string;
    coords?: string;
    notes?: string;
    edit: boolean;
  }> {
    return {
      handle,
      method: Method.Post,
      URL: "/admin/location/add",
      payload: {
        id: `${id}`,
        name: params.Name.trim(),
        locId: params.Code.trim(),
        address: params.Address?.trim() || undefined,
        coords: params.Coord?.trim() || undefined,
        notes: params.Notes?.trim() || undefined,
        edit: true,
      },
    };
  },
  makeResponse: castResponse<RecordResponse<string>>,
});

export const BackRackItemToUnrack_RequestTypes = createTyper({
  makeRequest(
    handle: RequestHandle,
    id: number
  ): RequestArgs<{ id: string }> {
    return {
      handle,
      method: Method.Post,
      URL: `/admin/unrack/back`,
      payload: { id: `${id}` },
    };
  },
  makeResponse: castResponse<RecordResponse<string>>,
});

export const GetUnrackList_RequestTypes = createTyper({
  makeRequest(
    handle: RequestHandle,
    limit?: number,
    offset?: number,
    search?: string,
    status?: MINER_STATUSES_E,
  ): RequestArgs<string> {
    const builder = new W2UiFilterBuilder().setLimit(limit ?? 0).setOffset(offset ?? 100);
    if (search && typeof search === "string") {
      builder.setSearch([
        SearchItem.textContains("searching", search.replaceAll("-", "")),
      ]);
    }
    if (status !== undefined) {
      builder.addSearch(SearchItem.intIs("status", status));
    }
    const params = builder.build().stringParams;
    return {
      handle,
      method: Method.Post,
      URL: `/admin/unrack/list`,
      payload: params,
    };
  },
  makeResponse: castResponse<RecordsResponse<IUnrackItem3>>,
});

export const DeleteRackTemplate_RequestTypes = createTyper({
  makeRequest(
    handle: RequestHandle,
    templateID: string
  ): RequestArgs<{ template_id: string }> {
    return {
      handle,
      method: Method.Post,
      URL: `/admin/rack/template/delete`,
      payload: { template_id: templateID },
    };
  },
});

interface BuildingMeta {
  ElectricScheme: unknown;
}

export interface BuildingUpdateParams3 {
  readonly id: string;
  readonly name?: string;
  readonly meta?: string;
}

export const EditBuilding_RequestTypes = createTyper({
  makeRequest(
    handle: RequestHandle,
    id: number,
    name?: string,
    meta?: string | BuildingMeta,
  ): RequestArgs<BuildingUpdateParams3> {
    if (meta !== undefined && typeof meta !== "string") {
      return {
        handle,
        method: Method.Post,
        URL: `/admin/object/update`,
        payload: {
          id: `${id}`,
          name,
          meta: JSON.stringify(meta),
        },
      };
    }
    return {
      handle,
      method: Method.Post,
      URL: `/admin/object/update`,
      payload: {
        id: `${id}`,
        name,
        meta,
      },
    };
  },
});

export const GetStatisticByLocation_RequestTypes = createTyper({
  makeRequest(
    handle: RequestHandle,
    dateFrom: Moment | null,
    dateTo: Moment | null,
    id: number,
    isAdmin?: boolean
  ): RequestArgs<DatePeriod & { location_id: string }> {
    const params = {
      ...createDatePeriodParamsEx3(dateFrom, dateTo),
      location_id: "" + id,
    };
    return {
      handle,
      method: Method.Post,
      URL: `/${isAdmin ? "admin" : "user"}/miner/statistic3`,
      payload: params,
    };
  },
  makeResponse: castResponse<RecordResponse<HashrateItem[]>>,
});


interface LocationResponse {
  id: number;
  meta: LocationMeta;
}

export const GetLocation_RequestTypes = createTyper({
  makeRequest(handle: RequestHandle, locationID: number) {
    return {
      handle,
      method: Method.Post,
      URL: "/admin/location/get",
      payload: { id: locationID }
    };
  },
  makeResponse: castResponse<RecordResponse<LocationResponse>>,
});

